import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  CircularProgress,
  MenuItem,
  styled
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { Link } from 'react-router-dom';
import TimeSlotGrid from './TimeSlotGrid';
import { saveDemoBooking, getAvailableTimeSlots } from '../../services/demoScheduling';
import { BUSINESS_TYPES, COUNTRIES, OTHER_BUSINESS_TYPE } from './demoFormOptions';
import GradientButton from '../../../components/buttons/GradientButton';

const FormCard = styled(Box)(({ theme }) => ({
  borderRadius: 16,
  padding: theme.spacing(3),
  boxShadow: '0 8px 32px rgba(0, 82, 204, 0.12)',
  background: 'linear-gradient(to bottom, #ffffff 0%, #f8faff 100%)',
  border: '1px solid rgba(0, 82, 204, 0.08)',
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 12,
    transition: 'all 0.3s ease',
    background: '#ffffff',
    '&:hover': {
      boxShadow: '0 2px 8px rgba(0, 82, 204, 0.08)',
    },
    '&.Mui-focused': {
      boxShadow: '0 4px 12px rgba(0, 82, 204, 0.12)',
    },
    '&:hover fieldset': {
      borderColor: '#0052CC',
      borderWidth: '2px',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#0052CC',
      borderWidth: '2px',
    }
  },
  '& .MuiInputLabel-root': {
    color: '#42526E',
    '&.Mui-focused': {
      color: '#0052CC',
      fontWeight: 500
    }
  }
}));

const RequiredLabel = styled('span')(({ theme }) => ({
  color: theme.palette.error.main,
  marginLeft: '2px',
}));

const SuccessIcon = styled(CheckCircleIcon)(({ theme }) => ({
  fontSize: 64,
  color: '#2E7D32',
  marginBottom: theme.spacing(2)
}));

const initialFormState = {
  name: '',
  email: '',
  companyName: '',
  phoneNumber: '',
  country: '',
  businessType: '',
  businessTypeOther: '',
};

const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

const formatPhoneNumber = (value) => {
  if (!value) return value;

  const phoneNumber = value.replace(/[^\d]/g, '');
  const phoneNumberLength = phoneNumber.length;
  if (phoneNumberLength < 4) return phoneNumber;
  if (phoneNumberLength < 7) {
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
  }
  return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
};

const validatePhone = (phone) => {
  if (!phone) {
    return 'Phone number is required';
  }
  const digitsOnly = phone.replace(/[^\d]/g, '');
  if (digitsOnly.length !== 10) {
    return 'Please enter a complete phone number';
  }
  return '';
};

const validateField = (name, value) => {
  if (!value.trim()) {
    return `${name} is required`;
  }
  return '';
};

const DemoSchedulingForm = () => {
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [companyError, setCompanyError] = useState('');
  const [nameError, setNameError] = useState('');
  const [countryError, setCountryError] = useState('');
  const [businessTypeError, setBusinessTypeError] = useState('');
  const [businessTypeOtherError, setBusinessTypeOtherError] = useState('');
  const [formData, setFormData] = useState(initialFormState);
  const [userTimezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    if (selectedDate) {
      fetchAvailableSlots(selectedDate);
    }
  }, [selectedDate]);

  const fetchAvailableSlots = async (date) => {
    try {
      setLoading(true);
      const slots = await getAvailableTimeSlots(date);
      setAvailableSlots(slots);
    } catch (err) {
      setError('Failed to load available time slots');
    } finally {
      setLoading(false);
    }
  };

  const validateEmail = (email) => {
    if (!email) {
      return 'Email is required';
    }
    if (!emailRegex.test(email)) {
      return 'Please enter a valid email address';
    }
    return '';
  };

  const handleTimeSelect = (time) => {
    setSelectedTime(time);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === 'phoneNumber') {
      const formattedPhone = formatPhoneNumber(value);
      if (!value || formattedPhone) {
        setFormData(prev => ({
          ...prev,
          [name]: formattedPhone
        }));
      }
    } else if (name === 'businessType') {
      setFormData(prev => ({
        ...prev,
        businessType: value,
        businessTypeOther: value === OTHER_BUSINESS_TYPE ? prev.businessTypeOther : '',
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }

    switch (name) {
      case 'email':
        setEmailError('');
        break;
      case 'phoneNumber':
        setPhoneError('');
        break;
      case 'companyName':
        setCompanyError('');
        break;
      case 'name':
        setNameError('');
        break;
      case 'country':
        setCountryError('');
        break;
      case 'businessType':
        setBusinessTypeError('');
        if (value !== OTHER_BUSINESS_TYPE) {
          setBusinessTypeOtherError('');
        }
        break;
      case 'businessTypeOther':
        setBusinessTypeOtherError('');
        break;
      default:
        break;
    }
  };

  const handleSubmit = async () => {
    try {
      const emailValidationError = validateEmail(formData.email);
      const phoneValidationError = validatePhone(formData.phoneNumber);
      const nameValidationError = validateField('Name', formData.name);
      const companyValidationError = validateField('Company name', formData.companyName);
      const countryValidationError = validateField('Country', formData.country);
      const businessTypeValidationError = validateField('Business type', formData.businessType);
      const businessTypeOtherValidationError = formData.businessType === OTHER_BUSINESS_TYPE
        ? validateField('Business description', formData.businessTypeOther)
        : '';

      setEmailError(emailValidationError);
      setPhoneError(phoneValidationError);
      setNameError(nameValidationError);
      setCompanyError(companyValidationError);
      setCountryError(countryValidationError);
      setBusinessTypeError(businessTypeValidationError);
      setBusinessTypeOtherError(businessTypeOtherValidationError);

      if (
        emailValidationError
        || phoneValidationError
        || nameValidationError
        || companyValidationError
        || countryValidationError
        || businessTypeValidationError
        || businessTypeOtherValidationError
      ) {
        return;
      }

      setLoading(true);
      setError('');

      if (!selectedDate || !selectedTime) {
        setError('Please select a date and time');
        return;
      }

      const localDateTime = new Date(selectedDate);
      const [time, period] = selectedTime.toLowerCase().split(' ');
      const [hours, minutes] = time.split(':');
      let hour = parseInt(hours, 10);

      if (period === 'pm' && hour !== 12) {
        hour += 12;
      } else if (period === 'am' && hour === 12) {
        hour = 0;
      }

      localDateTime.setHours(hour, parseInt(minutes, 10), 0, 0);

      try {
        const bookingPayload = {
          ...formData,
          dateTime: localDateTime,
          timezone: userTimezone,
        };

        if (bookingPayload.businessType !== OTHER_BUSINESS_TYPE) {
          delete bookingPayload.businessTypeOther;
        }

        await saveDemoBooking(bookingPayload);

        setShowSuccess(true);
      } catch (err) {
        if (err.message.includes('no longer available')) {
          setError(err.message);
          fetchAvailableSlots(selectedDate);
        } else {
          setError('Failed to schedule demo. Please try again.');
          console.error('Error scheduling demo:', err);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  if (showSuccess) {
    return (
      <FormCard sx={{ textAlign: 'center', py: 4 }}>
        <SuccessIcon />
        <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
          Demo Scheduled Successfully!
        </Typography>
        <Typography variant="body1" sx={{ mb: 4, color: '#42526E' }}>
          Thank you for scheduling a demo with us. We&apos;ve sent you a confirmation email with all the details.
        </Typography>
        <GradientButton
          component={Link}
          to="/"
          variant="primary"
          fullWidth
        >
          Back to Home
        </GradientButton>
      </FormCard>
    );
  }

  return (
    <FormCard>
      <Box>
        <Typography
          variant="caption"
          sx={{
            color: '#42526E',
            display: 'block',
            mb: 1,
            textAlign: 'center',
            fontSize: '0.9rem'
          }}
        >
          All times shown in your local timezone ({userTimezone})
        </Typography>

        <StyledTextField
          fullWidth
          label={<>Name<RequiredLabel>*</RequiredLabel></>}
          name="name"
          value={formData.name}
          onChange={handleInputChange}
          margin="normal"
          error={!!nameError}
          helperText={nameError}
          sx={{ mb: 2 }}
        />

        <StyledTextField
          fullWidth
          label={<>Email<RequiredLabel>*</RequiredLabel></>}
          name="email"
          type="email"
          value={formData.email}
          onChange={handleInputChange}
          margin="normal"
          error={!!emailError}
          helperText={emailError}
          sx={{ mb: 2 }}
        />

        <StyledTextField
          fullWidth
          label={<>Phone Number<RequiredLabel>*</RequiredLabel></>}
          name="phoneNumber"
          type="tel"
          value={formData.phoneNumber}
          onChange={handleInputChange}
          margin="normal"
          error={!!phoneError}
          helperText={phoneError || 'Format: (555) 555-5555'}
          inputProps={{
            maxLength: 14,
            placeholder: '(555) 555-5555'
          }}
          sx={{ mb: 2 }}
        />

        <StyledTextField
          fullWidth
          label={<>Company Name<RequiredLabel>*</RequiredLabel></>}
          name="companyName"
          value={formData.companyName}
          onChange={handleInputChange}
          margin="normal"
          error={!!companyError}
          helperText={companyError}
          sx={{ mb: 2 }}
        />

        <StyledTextField
          select
          fullWidth
          label={<>Country<RequiredLabel>*</RequiredLabel></>}
          name="country"
          value={formData.country}
          onChange={handleInputChange}
          margin="normal"
          error={!!countryError}
          helperText={countryError}
          sx={{ mb: 2 }}
        >
          <MenuItem value="" disabled>
            Select a country
          </MenuItem>
          {COUNTRIES.map((country) => (
            <MenuItem key={country} value={country}>
              {country}
            </MenuItem>
          ))}
        </StyledTextField>

        <StyledTextField
          select
          fullWidth
          label={<>What kind of business?<RequiredLabel>*</RequiredLabel></>}
          name="businessType"
          value={formData.businessType}
          onChange={handleInputChange}
          margin="normal"
          error={!!businessTypeError}
          helperText={businessTypeError}
          sx={{ mb: formData.businessType === OTHER_BUSINESS_TYPE ? 2 : 3 }}
        >
          <MenuItem value="" disabled>
            Select a business type
          </MenuItem>
          {BUSINESS_TYPES.map((type) => (
            <MenuItem key={type} value={type}>
              {type}
            </MenuItem>
          ))}
        </StyledTextField>

        {formData.businessType === OTHER_BUSINESS_TYPE && (
          <StyledTextField
            fullWidth
            label={<>Please specify<RequiredLabel>*</RequiredLabel></>}
            name="businessTypeOther"
            value={formData.businessTypeOther}
            onChange={handleInputChange}
            margin="normal"
            error={!!businessTypeOtherError}
            helperText={businessTypeOtherError}
            placeholder="e.g. Pet grooming, tutoring, photography"
            sx={{ mb: 3 }}
          />
        )}

        <Box sx={{ mb: 3 }}>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 600,
              color: '#0052CC',
              mb: 2,
              textAlign: 'center'
            }}
          >
            Select Date
          </Typography>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box display="flex" justifyContent="center">
              <DatePicker
                value={selectedDate}
                onChange={(newValue) => {
                  setSelectedDate(newValue);
                  setSelectedTime(null);
                }}
                disablePast
                renderInput={(params) => <StyledTextField {...params} sx={{ width: '100%', maxWidth: '400px' }} />}
                inputFormat="MM/dd/yyyy"
              />
            </Box>
          </LocalizationProvider>
        </Box>

        {selectedDate && (
          <Box
            sx={{
              mt: 3,
              p: 3,
              borderRadius: 2
            }}
          >
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                color: '#0052CC',
                mb: 2,
                textAlign: 'center'
              }}
            >
              Available Time Slots
            </Typography>
            {loading ? (
              <Box display="flex" justifyContent="center" my={3}>
                <CircularProgress sx={{ color: '#0052CC' }} />
              </Box>
            ) : (
              <TimeSlotGrid
                timeSlots={availableSlots}
                selectedTime={selectedTime}
                onTimeSelect={handleTimeSelect}
              />
            )}
          </Box>
        )}

        {error && (
          <Typography
            color="error"
            variant="body2"
            sx={{
              mt: 2,
              textAlign: 'center',
              backgroundColor: '#FFEBE6',
              padding: 2,
              borderRadius: 1
            }}
          >
            {error}
          </Typography>
        )}
      </Box>

      <Box sx={{
        paddingTop: 3,
        gap: 2,
        display: 'flex',
        justifyContent: 'center',
        flexWrap: 'wrap'
      }}>
        <GradientButton
          component={Link}
          to="/"
          variant="primary"
        >
          Cancel
        </GradientButton>
        <GradientButton
          onClick={handleSubmit}
          disabled={
            loading
            || !selectedDate
            || !selectedTime
            || !formData.email
            || !formData.name
            || !formData.phoneNumber
            || !formData.companyName
            || !formData.country
            || !formData.businessType
            || (formData.businessType === OTHER_BUSINESS_TYPE && !formData.businessTypeOther.trim())
          }
        >
          {loading ? <CircularProgress size={24} sx={{ color: '#fff' }} /> : 'Confirm'}
        </GradientButton>
      </Box>
    </FormCard>
  );
};

export default DemoSchedulingForm;
